import { addDays, today } from '../lib/date'
import { uid } from '../lib/id'
import { INTAKE_PLANS } from './intakes'
import type { AppState, IntakeId } from './types'

type Lang = 'en' | 'fa'

/** Picks the string for the active language. Seeded *content* is real data the
 *  user can edit, so it is authored in both languages rather than translated at
 *  render time — once edited, it is simply their text. */
const L = (lang: Lang, en: string, fa: string) => (lang === 'fa' ? fa : en)

/**
 * First-run content: Ala's pathway — Kermanshah → Muscat → Adelaide, targeting
 * a Bachelor of Music (Creative Practice) at the Elder Conservatorium.
 *
 * Dates come from `intakes.ts` and are planning targets, not published
 * deadlines. That caveat is repeated inside the seeded wiki so it stays visible
 * in the app rather than only in the source.
 *
 * Wiki pages are the full text of the 15-module study guide (EN + FA versions),
 * replacing the original 5-page set. Tasks are the Module-13 month-by-month
 * action plan. Application requirements include the Module-14 document checklist.
 */
export function seedState(lang: Lang = 'en', intake: IntakeId = 'aug2027'): AppState {
  const t = today()
  const plan = INTAKE_PLANS[intake]
  const { docsReady, applyBy, auditionRound, visaLodge, flights, orientation } = plan

  const subEnglish = uid('sub')
  const subRepertoire = uid('sub')
  const subMusicianship = uid('sub')
  const subProduction = uid('sub')

  const appCon = uid('app')
  const appFoundation = uid('app')
  const appFlinders = uid('app')
  const appTorrens = uid('app')

  return {
    version: 1,
    settings: {
      name: L(lang, 'Ala', 'الا'),
      city: L(lang, 'Muscat', 'مسقط'),
      lang,
      targetIntake: intake,
      themeMode: 'system',
      accent: '#1539F6',
      tileOrder: [
        'countdown',
        'nextUp',
        'tasks',
        'studyWeek',
        'audition',
        'quickNote',
        'weather',
        'player',
      ],
      hiddenTiles: [],
      wideTiles: ['countdown', 'nextUp', 'tasks', 'quickNote'],
      focusMins: 25,
      breakMins: 5,
    },

    subjects: [
      {
        id: subEnglish,
        name: L(lang, 'English / IELTS', 'انگلیسی / آیلتس'),
        hue: 'clay',
        targetMins: 300,
      },
      {
        id: subRepertoire,
        name: L(lang, 'Audition repertoire', 'رپرتوار آزمون اجرا'),
        hue: 'iris',
        targetMins: 420,
      },
      {
        id: subMusicianship,
        name: L(lang, 'Musicianship & theory', 'تئوری و مهارت موسیقی'),
        hue: 'sage',
        targetMins: 180,
      },
      {
        id: subProduction,
        name: L(lang, 'Recording & production', 'ضبط و تولید'),
        hue: 'teal',
        targetMins: 240,
      },
    ],

    // Module 13 — month-by-month action plan (replacing the original task set)
    tasks: [
      // Now – Aug 2026
      {
        id: uid('t'),
        title: L(
          lang,
          'Complete Year 12',
          'سال دوازدهم را تمام کن',
        ),
        due: addDays(t, 7),
        done: false,
        priority: 1,
        createdAt: Date.now(),
      },
      {
        id: uid('t'),
        title: L(
          lang,
          'Take a free IELTS/PTE practice test online to find starting level',
          'یک آزمون آزمایشی رایگان IELTS یا PTE آنلاین بده تا سطح شروعت را بفهمی',
        ),
        subjectId: subEnglish,
        due: addDays(t, 7),
        done: false,
        priority: 1,
        createdAt: Date.now(),
      },
      {
        id: uid('t'),
        title: L(
          lang,
          'Shortlist 3 pathways: Adelaide Foundation, Flinders Foundation, one TAFE packaged offer',
          'سه مسیر را کوتاه‌فهرست کن: Foundation آدلاید، Foundation فلیندرز، یک پیشنهاد بسته‌ای TAFE',
        ),
        due: addDays(t, 14),
        done: false,
        priority: 1,
        createdAt: Date.now(),
      },
      // Aug – Oct 2026
      {
        id: uid('t'),
        title: L(
          lang,
          'Begin structured IELTS/PTE prep — target band 5.5–6.0',
          'آماده‌سازی منظم IELTS/PTE را شروع کن — هدف نمره‌ی ۵٫۵ تا ۶٫۰',
        ),
        subjectId: subEnglish,
        due: addDays(t, 30),
        done: false,
        priority: 1,
        createdAt: Date.now(),
      },
      {
        id: uid('t'),
        title: L(
          lang,
          'Gather documents: transcripts, passport, Year 11/12 results',
          'مدارک را جمع کن: ریزنمرات، پاسپورت، نتایج یازدهم و دوازدهم',
        ),
        due: addDays(t, 45),
        done: false,
        priority: 1,
        createdAt: Date.now(),
      },
      {
        id: uid('t'),
        title: L(
          lang,
          'Certified English translation of all transcripts and diploma',
          'ترجمه‌ی رسمی انگلیسی تمام کارنامه‌ها و دیپلم',
        ),
        due: addDays(t, 45),
        done: false,
        priority: 1,
        createdAt: Date.now(),
      },
      {
        id: uid('t'),
        title: L(
          lang,
          'Contact international offices — ask about Iranian payment channels + conditional English-bundled offers',
          'با دفاتر بین‌الملل تماس بگیر — درباره‌ی کانال‌های پرداخت ویژه‌ی ایرانیان و پیشنهادهای مشروط همراه با دوره‌ی انگلیسی بپرس',
        ),
        due: addDays(t, 60),
        done: false,
        priority: 0,
        createdAt: Date.now(),
      },
      // Oct – Dec 2026
      {
        id: uid('t'),
        title: L(
          lang,
          'Submit foundation/pathway applications (free to hold)',
          'درخواست‌های Foundation/مسیر را ارسال کن (نگه‌داشتن رایگان است)',
        ),
        due: applyBy,
        done: false,
        priority: 1,
        createdAt: Date.now(),
      },
      {
        id: uid('t'),
        title: L(lang, 'Sit IELTS/PTE exam', 'در آزمون IELTS/PTE شرکت کن'),
        subjectId: subEnglish,
        due: applyBy,
        done: false,
        priority: 1,
        createdAt: Date.now(),
      },
      {
        id: uid('t'),
        title: L(
          lang,
          'Register for the Elder Conservatorium audition round (same season)',
          'برای دور آزمون اجرای کنسرواتوار الدر ثبت‌نام کن (همین فصل)',
        ),
        subjectId: subRepertoire,
        due: applyBy,
        done: false,
        priority: 0,
        createdAt: Date.now(),
      },
      // Dec 2026 – Jan 2027
      {
        id: uid('t'),
        title: L(
          lang,
          'Accept offer + pay deposit → receive Confirmation of Enrolment (CoE)',
          'پیشنهاد را قبول کن و پیش‌پرداخت بده ← تأییدیه‌ی ثبت‌نام (CoE) بگیر',
        ),
        due: addDays(visaLodge, -60),
        done: false,
        priority: 1,
        createdAt: Date.now(),
      },
      {
        id: uid('t'),
        title: L(
          lang,
          'Arrange OSHC health insurance + compile financial evidence (use Oman bank account)',
          'بیمه‌ی سلامت OSHC را ترتیب بده و مدارک مالی را جمع کن (از حساب بانکی عمان استفاده کن)',
        ),
        due: addDays(visaLodge, -30),
        done: false,
        priority: 1,
        createdAt: Date.now(),
      },
      {
        id: uid('t'),
        title: L(
          lang,
          'Draft Genuine Student statements: why this course, why Australia, career plans back home',
          'متن‌های Genuine Student را پیش‌نویس کن: چرا این رشته، چرا استرالیا، برنامه‌ی شغلی در وطن',
        ),
        due: visaLodge,
        done: false,
        priority: 1,
        createdAt: Date.now(),
      },
      // Jan – Feb 2027
      {
        id: uid('t'),
        title: L(
          lang,
          'Lodge Student Visa Subclass 500 with Genuine Student statements',
          'ویزای دانشجویی Subclass 500 را با متن‌های Genuine Student ثبت کن',
        ),
        due: visaLodge,
        done: false,
        priority: 1,
        createdAt: Date.now(),
      },
      {
        id: uid('t'),
        title: L(lang, 'Book flights and accommodation for Adelaide', 'بلیت و اقامت برای آدلاید رزرو کن'),
        due: flights,
        done: false,
        priority: 0,
        createdAt: Date.now(),
      },
      {
        id: uid('t'),
        title: L(
          lang,
          'Record the audition video (overseas recorded audition accepted)',
          'ویدیوی آزمون اجرا را ضبط کن (آزمون ضبط‌شده از خارج پذیرفته می‌شود)',
        ),
        subjectId: subProduction,
        due: auditionRound,
        done: false,
        priority: 0,
        createdAt: Date.now(),
      },
    ],

    sessions: [],

    events: [
      {
        id: uid('e'),
        title: L(
          lang,
          'Target: documents translated, English test sat, repertoire ready',
          'هدف: ترجمه‌ی مدارک، آزمون انگلیسی، آمادگی رپرتوار',
        ),
        date: docsReady,
        kind: 'deadline',
      },
      {
        id: uid('e'),
        title: L(
          lang,
          'Target: applications submitted + audition round registered',
          'هدف: ارسال درخواست‌ها و ثبت‌نام دور آزمون',
        ),
        date: applyBy,
        kind: 'deadline',
        applicationId: appFoundation,
        notes: L(
          lang,
          'Applying is free. The double play — foundation program AND audition, same season.',
          'ثبت درخواست رایگان است. بازی دوگانه — هم دوره‌ی پیش‌دانشگاهی، هم آزمون اجرا، در یک فصل.',
        ),
      },
      {
        id: uid('e'),
        title: L(lang, 'Audition round', 'دور آزمون اجرا'),
        date: auditionRound,
        kind: 'audition',
        applicationId: appCon,
        notes: L(
          lang,
          'Recorded audition accepted from overseas — record it in Muscat. No audition fee.',
          'آزمون ضبط‌شده از خارج پذیرفته می‌شود — در مسقط ضبطش کن. هزینه‌ای ندارد.',
        ),
      },
      {
        id: uid('e'),
        title: L(lang, 'Lodge Student Visa (Subclass 500)', 'ثبت درخواست ویزای دانشجویی (۵۰۰)'),
        date: visaLodge,
        kind: 'deadline',
        notes: L(
          lang,
          'CoE, financial evidence, Genuine Student statements. Lodge early — allow months.',
          'CoE، مدارک مالی، متن‌های Genuine Student. زود اقدام کن — چند ماه وقت بگذار.',
        ),
      },
      {
        id: uid('e'),
        title: L(lang, 'Flights and housing sorted', 'بلیت و مسکن نهایی'),
        date: flights,
        kind: 'personal',
      },
      {
        id: uid('e'),
        title: L(lang, 'Orientation week — Adelaide', 'هفته‌ی آشنایی — آدلاید'),
        date: orientation,
        kind: 'personal',
      },
    ],

    applications: [
      {
        id: appCon,
        institution: L(
          lang,
          'Adelaide University — Elder Conservatorium',
          'دانشگاه آدلاید — کنسرواتوار الدر',
        ),
        course: L(lang, 'Bachelor of Music (Creative Practice)', 'کارشناسی موسیقی (اجرای خلاق)'),
        deadline: auditionRound,
        status: 'preparing',
        notes: L(
          lang,
          'Selection is on audition score, not marks — the academic threshold is covered by the foundation pathway. Popular music, songwriting, vocals, production. IELTS 6.5 required. No audition fee. Overseas recorded audition accepted.',
          'انتخاب بر اساس نمره‌ی آزمون اجراست، نه معدل — حد نصاب درسی را دوره‌ی پیش‌دانشگاهی پوشش می‌دهد. موسیقی پاپ، ترانه‌سرایی، آواز، تولید. آیلتس ۶٫۵ لازم است. هزینه‌ی آزمون ندارد. آزمون ضبط‌شده از خارج پذیرفته می‌شود.',
        ),
        requirements: [
          {
            id: uid('r'),
            label: L(lang, 'Check audition requirements by program (vocals/creative practice)', 'بررسی الزامات آزمون هر رشته (آواز / اجرای خلاق)'),
            done: false,
          },
          { id: uid('r'), label: L(lang, 'Choose audition repertoire', 'انتخاب رپرتوار آزمون'), done: false },
          {
            id: uid('r'),
            label: L(lang, 'Register online for an audition round', 'ثبت‌نام آنلاین برای دور آزمون'),
            done: false,
            due: applyBy,
          },
          {
            id: uid('r'),
            label: L(lang, 'Record and submit audition video (overseas recording accepted)', 'ضبط و ارسال ویدیوی آزمون اجرا (ضبط از خارج پذیرفته می‌شود)'),
            done: false,
            due: auditionRound,
          },
          {
            id: uid('r'),
            label: L(lang, 'Sit the online musicianship test (aural, written, theory)', 'شرکت در آزمون آنلاین موسیقی (شنیداری، کتبی، تئوری)'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'IELTS/PTE result: 6.5+ (no band under 6.0)', 'نتیجه‌ی IELTS/PTE: ۶٫۵+ (هیچ بخش زیر ۶٫۰)'),
            done: false,
          },
        ],
      },
      {
        id: appFoundation,
        institution: L(
          lang,
          'Adelaide University — Foundation Studies (Kaplan KICA)',
          'دانشگاه آدلاید — دوره‌ی پیش‌دانشگاهی (کاپلان KICA)',
        ),
        course: L(
          lang,
          '8–12 month pathway · Feb & Aug intakes · ~AUD $30,000–33,000',
          '۸ تا ۱۲ ماهه · ترم‌های فوریه و اوت · حدود ۳۰٬۰۰۰ تا ۳۳٬۰۰۰ دلار استرالیا',
        ),
        deadline: applyBy,
        status: 'researching',
        notes: L(
          lang,
          'The headline recommendation. Ignores weak Year 12 marks. Needs only IELTS 5.5. Ends in guaranteed degree entry. Compare Kaplan (KICA) vs Eynesbury — Eynesbury Diploma enters year 2. Apply early; holding an offer is free. Also check the Flinders Foundation (Module 3) as a parallel application.',
          'توصیه‌ی اصلی. نمرات ضعیف دوازدهم را نادیده می‌گیرد. فقط آیلتس ۵٫۵ می‌خواهد. به ورود تضمینی می‌رسد. کاپلان (KICA) را با اینزبری مقایسه کن — دیپلم اینزبری به سال دوم می‌رسد. زود درخواست بده؛ نگه‌داشتن پیشنهاد رایگان است. دوره‌ی پیش‌دانشگاهی فلیندرز (ماژول ۳) را هم به‌صورت موازی درخواست بده.',
        ),
        requirements: [
          // Application requirements
          {
            id: uid('r'),
            label: L(lang, 'Compare Kaplan (KICA) vs Eynesbury foundation programs', 'مقایسه‌ی دوره‌های پیش‌دانشگاهی کاپلان (KICA) و اینزبری'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'Personal statement — include circumstances that affected Year 12 marks', 'انگیزه‌نامه — شرایطی که روی نمرات دوازدهم اثر گذاشت را بنویس'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'Submit application (free)', 'ارسال درخواست (رایگان)'),
            done: false,
            due: applyBy,
          },
          // Module 14 — Document Checklist
          {
            id: uid('r'),
            label: L(lang, 'Passport (valid well beyond 2027)', 'پاسپورت (معتبر تا مدتی پس از ۲۰۲۷)'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'Year 11 results + Year 12 completion certificate', 'کارنامه‌ی سال یازدهم + مدرک پایان دوازدهم'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'Certified English translations of all transcripts', 'ترجمه‌ی رسمی انگلیسی همه‌ی کارنامه‌ها'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'IELTS/PTE result (target 5.5+, no band under 5.0)', 'نتیجه‌ی IELTS/PTE (هدف ۵٫۵+، هیچ بخش زیر ۵٫۰)'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'Financial evidence (bank statements — ideally Oman account, stable history)', 'مدارک مالی (صورت‌حساب بانکی — ترجیحاً حساب عمان با سابقه‌ی باثبات)'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'Draft Genuine Student statements (≈150 words each: course, Australia, career)', 'پیش‌نویس متن‌های Genuine Student (حدود ۱۵۰ کلمه هرکدام: رشته، استرالیا، شغل)'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'Passport photos', 'عکس پاسپورتی'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'OSHC health insurance (arranged at CoE stage)', 'بیمه‌ی سلامت OSHC (در مرحله‌ی CoE ترتیب داده می‌شود)'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'CV/résumé (useful for pathway and scholarship applications)', 'رزومه (برای بعضی درخواست‌های مسیر و بورسیه مفید است)'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'Contact college about approved Iranian payment channels before paying anything', 'پیش از پرداخت هر پولی با دفتر بین‌الملل درباره‌ی کانال‌های پرداخت موردتأیید ایرانیان صحبت کن'),
            done: false,
          },
        ],
      },
      {
        id: appFlinders,
        institution: L(lang, 'Flinders University', 'دانشگاه فلیندرز'),
        course: L(
          lang,
          'International Foundation Studies · Bedford Park · ~AUD $30,500 · Auto 20% scholarship',
          'دوره‌ی پیش‌دانشگاهی بین‌المللی · بدفورد پارک · حدود ۳۰٬۵۰۰ دلار استرالیا · بورسیه‌ی خودکار ۲۰٪',
        ),
        deadline: applyBy,
        status: 'researching',
        notes: L(
          lang,
          'Strong backup. Automatic 20% off foundation; up to 30% off the later degree on performance. Guaranteed entry to 50+ bachelor degrees. ELICOS routes avoid an IELTS test entirely. Strong in science, health and nursing. Apply in parallel with Adelaide — holding is free.',
          'گزینه‌ی پشتیبان قوی. بورسیه‌ی خودکار ۲۰٪ روی Foundation؛ تا ۳۰٪ روی کارشناسی بعدی بر اساس عملکرد. ورود تضمینی به بیش از ۵۰ رشته‌ی کارشناسی. مسیر ELICOS اصلاً نیاز به آیلتس ندارد. در علوم سلامت و پرستاری قوی است. به‌صورت موازی با آدلاید درخواست بده — نگه‌داشتن رایگان است.',
        ),
        requirements: [
          {
            id: uid('r'),
            label: L(lang, 'Check the test-free ELICOS English pathway (no IELTS required)', 'بررسی مسیر انگلیسی بدون آزمون ELICOS (نیاز به آیلتس ندارد)'),
            done: false,
          },
          {
            id: uid('r'),
            label: L(lang, 'Confirm automatic 20% scholarship conditions', 'شرایط بورسیه‌ی خودکار ۲۰٪ را تأیید کن'),
            done: false,
          },
          { id: uid('r'), label: L(lang, 'Submit application', 'ارسال درخواست'), done: false },
        ],
      },
      {
        id: appTorrens,
        institution: L(lang, 'Torrens University', 'دانشگاه تورنز'),
        course: L(lang, 'Creative industries · CBD campuses · No-ATAR entry', 'صنایع خلاق · پردیس مرکز شهر · ورود بدون ATAR'),
        deadline: addDays(applyBy, 30),
        status: 'researching',
        notes: L(
          lang,
          'Plan C. Private university, flexible individual admissions, multiple intakes. Portfolio/interview-based entry for creative courses — suits a confident, expressive student. Removes ATAR anxiety entirely. Multiple intakes if timing gets tight.',
          'نقشه‌ی سوم. دانشگاه خصوصی، پذیرش موردی و منعطف، چند ترم در سال. ورود بر اساس نمونه‌کار/مصاحبه برای رشته‌های خلاق — مناسب یک دانشجوی بااعتمادبه‌نفس و بیانگر. اضطراب ATAR را کاملاً حذف می‌کند. اگر زمان تنگ شد چند ترم دارد.',
        ),
        requirements: [
          { id: uid('r'), label: L(lang, 'Check intake dates and entry options (portfolio / readiness test)', 'بررسی تاریخ ترم‌ها و گزینه‌های ورود (نمونه‌کار / آزمون آمادگی)'), done: false },
        ],
      },
    ],

    tracks: [
      {
        id: uid('tr'),
        title: L(lang, 'Audition piece 1', 'قطعه‌ی آزمون ۱'),
        artist: L(lang, 'Ala — draft', 'الا — پیش‌نویس'),
        durationSec: 0,
        hue: 'iris',
        audition: true,
      },
      {
        id: uid('tr'),
        title: L(lang, 'Audition piece 2', 'قطعه‌ی آزمون ۲'),
        artist: L(lang, 'Ala — not started', 'الا — شروع نشده'),
        durationSec: 0,
        hue: 'rose',
        audition: true,
      },
    ],

    wiki: wikiPages(lang),

    notes: [
      {
        id: uid('n'),
        // Module 11 — budget snapshot
        text: L(
          lang,
          'Budget/yr (AUD, approx): Foundation ~$30–33k · Science/Arts degree ~$38–48k · Music degree ~$45.6k · Living costs (visa min.) ~$29.7k · OSHC ~$600–1k · Visa fee ~$2k · Adelaide rent noticeably cheaper than Sydney/Melbourne.',
          'بودجه در سال (دلار استرالیا، تقریبی): Foundation حدود ۳۰ تا ۳۳ هزار · کارشناسی علوم/هنر حدود ۳۸ تا ۴۸ هزار · کارشناسی موسیقی حدود ۴۵٬۶۰۰ · هزینه‌ی زندگی (حداقل ویزا) حدود ۲۹٬۷۱۰ · OSHC حدود ۶۰۰ تا ۱٬۰۰۰ · هزینه‌ی ویزا حدود ۲٬۰۰۰ · اجاره‌ی آدلاید به‌طور محسوسی ارزان‌تر از سیدنی/ملبورن.',
        ),
        hue: 'amber',
        createdAt: Date.now(),
      },
      {
        id: uid('n'),
        text: L(
          lang,
          'Every date in here is a planning target, not a published university deadline. Confirm all dates directly with the institutions before applying or paying.',
          'همه‌ی تاریخ‌های اینجا هدف‌های برنامه‌ریزی‌شده‌اند، نه مهلت‌های اعلام‌شده‌ی دانشگاه. پیش از درخواست یا پرداخت، همه را مستقیم با مؤسسه‌ها تأیید کن.',
        ),
        hue: 'amber',
        createdAt: Date.now(),
      },
    ],

    quickNote: '',
  }
}

/* ------------------------------------------------------------------- wiki */

/**
 * A stable id for a seed page, derived from its title.
 *
 * These used to be `uid('w')`, regenerated on every load, which meant a stored
 * page could never be matched to the seed page it came from — so refreshing the
 * guide duplicated it instead of updating it. The id has to be a function of
 * the content for `mergeWiki` to work at all. Plain djb2; collisions across ~16
 * fixed titles are not a real risk, and the prefix keeps these distinguishable
 * from the `w_` ids `addWiki` mints for pages Ala writes herself.
 */
function seedPageId(title: string): string {
  let h = 5381
  for (let i = 0; i < title.length; i++) h = ((h << 5) + h + title.charCodeAt(i)) >>> 0
  return `w-${h.toString(36)}`
}

function wikiPages(lang: Lang): AppState['wiki'] {
  const now = Date.now()
  const page = (title: string, tag: string, body: string) => ({
    id: seedPageId(title),
    title,
    tag,
    body,
    updatedAt: now,
  })

  if (lang === 'fa') {
    return [
      /* ---- Module 0 ---- */
      page(
        'ماژول ۰ — استراتژی صادقانه',
        'مسیر',
        `# از اینجا شروع کن: استراتژی صادقانه

آلا، مهم‌ترین چیزی که باید اول بدانی این است: **نمرات پایین سال دوازدهم درِ دانشگاه‌های استرالیا را روی تو نمی‌بندد.** سیستم استرالیا دقیقاً برای وضعیت تو ساخته شده. سه مسیر قانونی و شناخته‌شده برای ورود به دانشگاه وجود دارد که دو تای آن‌ها تقریباً هیچ نگاهی به نمرات نهایی دبیرستان تو نمی‌اندازند:

۱. **دوره‌ی پیش‌دانشگاهی (Foundation Studies)** — یک سال آماده‌سازی ۸ تا ۱۲ ماهه. شرط ورود فقط **پایان سال یازدهم با حدود ۵۰٪** و انگلیسی پایه است. با گذراندنش، ورود *تضمین‌شده* به کارشناسی می‌گیری. این قوی‌ترین ابزار برای دانشجویی با نمرات ضعیف سال آخر است.

۲. **مسیر دیپلم / TAFE** — یک دیپلم فنی‌وحرفه‌ای یک تا دو ساله که هم خودش مدرک واقعی است و هم به‌عنوان واحد به سال اول یا دوم دانشگاه منتقل می‌شود. شرایط ورودش آسان است.

۳. **ورود مستقیم** به کارشناسی — تنها مسیری که نمرات در آن خیلی مهم‌اند؛ پس فعلاً آن را «نقشه‌ی C» می‌گذاریم، نه هدف.

نمرات پایین الان بزرگ به‌نظر می‌رسند، اما در سیستم مسیرهای استرالیا فقط یک دور زدن هستند، نه یک دیوار. کار تو در ۱۲ ماه آینده درست‌کردن گذشته نیست — ساختن یک **درخواست قویِ Foundation یا دیپلم** و یک **نمره‌ی خوب انگلیسی** است. همین دو چیز، نه معدل دوازدهم تو، سرنوشت ۲۰۲۷ را تعیین می‌کنند.

> 💡 **نکته:** دوازدهم را به‌هر‌حال تمام کن. حتی یک مدرک پایان دبیرستانِ معمولی، درخواست‌های Foundation و دیپلم را راحت‌تر می‌کند و گزینه‌ی ورود مستقیم را هم به‌عنوان پشتیبان زنده نگه می‌دارد. مدرک دبیرستانِ کامل‌شده همیشه بهتر از ناتمام است.`,
      ),

      /* ---- Module 1 ---- */
      page(
        'ماژول ۱ — واژه‌نامه‌ی سیستم استرالیا',
        'مسیر',
        `# سیستم استرالیا چطور کار می‌کند — واژه‌نامه

پنج واژه تقریباً همه‌چیز را باز می‌کند. این‌ها را یاد بگیر تا بقیه‌ی راهنما ساده شود.

| واژه | معنی ساده |
|---|---|
| **ATAR** | رتبه‌ای (۰ تا ۹۹٫۹۵) که مدارس استرالیا برای سال دوازدهم می‌دهند. آلا به‌عنوان دانشجوی بین‌المللی معمولاً **به ATAR نیاز ندارد**؛ دیپلم کشورش با جدول معادل‌سازی خودش سنجیده می‌شود. |
| **Foundation Studies** | یک سال پل‌زننده که جای نمرات قوی سال آخر را می‌گیرد و به ورود تضمینی به کارشناسی می‌رسد. |
| **Diploma / TAFE / VET** | مدارک فنی‌وحرفه‌ای (TAFE = شبکه‌ی کالج‌های دولتی؛ VET = کل بخش فنی‌وحرفه‌ای). هم مستقل ارزش دارند و هم با انتقال واحد به دانشگاه وصل می‌شوند. |
| **CRICOS** | فهرست رسمی دوره‌هایی که مجاز به پذیرش دانشجوی بین‌المللی‌اند. اگر دوره‌ای کد CRICOS داشته باشد، برای ویزا معتبر است. همیشه چک کن. |
| **CoE** | برگه‌ی تأیید ثبت‌نام. بعد از قبول و پرداخت پیش‌پرداخت صادر می‌شود. بدون آن نمی‌توانی ویزای دانشجویی بگیری. |
| **Genuine Student (GS)** | متن‌های کوتاهی در فرم ویزا که توضیح می‌دهد چرا درس می‌خوانی. از ۲۰۲۴ جایگزین «GTE» شد. |
| **OSHC** | بیمه‌ی سلامت خصوصی و اجباری دانشجویان بین‌المللی برای کل مدت ویزا. |

> 💡 **نکته:** وقتی به هر دوره‌ای نگاه می‌کنی، اول **کد CRICOS** و **شرط انگلیسی** آن را پیدا کن. همین دو خط فوراً به تو می‌گوید آن دوره برایت واقع‌بینانه است یا نه.`,
      ),

      /* ---- Module 2 ---- */
      page(
        'ماژول ۲ — دانشگاه آدلاید',
        'دانشگاه',
        `# اول آدلاید: دانشگاه آدلاید (Adelaide University)

یک تغییر مهم ۲۰۲۶ که باید بدانی: **دانشگاه آدلاید و دانشگاه استرالیای جنوبی (UniSA) در هم ادغام شدند و یک دانشگاه واحد به نام «Adelaide University» ساختند که ژانویه‌ی ۲۰۲۶ باز شد.** پس هر صفحه‌ی قدیمیِ «UniSA» یا «University of Adelaide» حالا به همین دانشگاه جدید می‌رسد. کالج مسیرِ رسمی‌اش **Kaplan International College Adelaide** است (که قبلاً «University of Adelaide College» نام داشت).

چرا این برای آلا مهم است؟ چون آدلاید حالا هم مدارک دانشگاه پژوهشی و هم یک نردبان کامل از دوره‌های پیش‌دانشگاهی را زیر یک سقف دارد، در شهری که ارزان‌تر و آرام‌تر از سیدنی و ملبورن است و (ماژول ۱۲ را ببین) از نظر مهاجرتی **منطقه‌ای (regional)** حساب می‌شود.

**دو درِ واقع‌بینانه به دانشگاه آدلاید:**

| در | چیست | ورود (تقریبی) | انگلیسی (تقریبی) | نتیجه |
|---|---|---|---|---|
| **Foundation Studies** (از طریق Kaplan) | سال آماده‌سازی ۸ تا ۱۲ ماهه | پایان سال یازدهم با ~۵۰٪ یا دوازدهم | آیلتس **۵٫۵** (هیچ بخش زیر ۵٫۰) | ورود تضمینی به سال اول اغلب کارشناسی‌های آدلاید |
| **Degree Transfer / Diploma** (از طریق Kaplan) | دیپلم معادل سال اول | ATAR ~۴۵–۵۰ یا Foundation ۴۰–۵۰٪ | آیلتس **۵٫۵** (بخش‌ها ۵٫۰ به بالا) | مستقیم به **سال دوم** کارشناسی |
| **ورود مستقیم** | مستقیم به کارشناسی | دوازدهم رقابتی (جدول کشوری) | آیلتس **۶٫۰** (بسته به رشته، بعضی بالاتر) | سال اول کارشناسی |

> 💡 **نکته:** برای آلا، درِ **Foundation Studies** آدلاید توصیه‌ی اصلی است. نمرات ضعیف دوازدهم را نادیده می‌گیرد، فقط آیلتس ۵٫۵ می‌خواهد و به جای تضمینیِ کارشناسی می‌رسد. اگر انگلیسی‌اش قوی‌تر باشد، **دیپلم Degree Transfer** یک سال کامل صرفه‌جویی می‌کند چون او را به سال دوم می‌اندازد.

- [شرایط ورود بین‌المللی آدلاید](https://adelaide.edu.au/study/international-students/how-to-apply/entry-requirements/)
- [Kaplan KICA — Foundation Studies](https://college.adelaide.edu.au/programs/foundation-studies/)`,
      ),

      /* ---- Module 3 ---- */
      page(
        'ماژول ۳ — دانشگاه فلیندرز',
        'دانشگاه',
        `# دانشگاه فلیندرز — گزینه‌ی دوم آدلاید

فلیندرز دانشگاه دولتی بزرگ دیگر آدلاید است و در ادغام شرکت **نکرد**؛ مسیر مستقل و بسیار در‌دسترسِ خودش را دارد. **برنامه‌ی Foundation بین‌المللی** آن برای پروفایل آلا عالی است:

| ویژگی | جزئیات |
|---|---|
| ورود (تحصیلی) | پایان **سال یازدهم** یا معادل؛ حداقل سن ۱۷ |
| انگلیسی | آیلتس **۵٫۵** (هیچ بخش زیر ۵٫۰) |
| مدت و شروع | **۱۲ ماهه** (شروع فوریه) یا **۸ ماهه** (شروع ژوئن و اکتبر) |
| شهریه | حدود **۳۰٬۵۰۰ دلار استرالیا** برای سال Foundation |
| نتیجه | ورود **تضمینی** به بیش از ۵۰ رشته‌ی کارشناسی (علوم، سلامت، پرستاری، بازرگانی، مهندسی و…) |
| بورسیه | **۲۰٪ تخفیف خودکار** روی Foundation؛ تا **۳۰٪** روی کارشناسی بعدی بر اساس عملکرد |
| انگلیسیِ ورود مستقیم | آیلتس **۶٫۰** (کارشناسی) برای کسانی که Foundation را رد می‌کنند |

چون رشته‌ی آلا **علوم** است، فلیندرز که در علوم سلامت و پرستاری قوی است گزینه‌ی خوبی برای فهرست کوتاه اوست؛ برنامه‌ی Foundation آن حوزه‌ی یادگیریِ علوم و ریاضی دارد که می‌تواند رویش تمرکز کند.

> 💡 **نکته:** هم به **Foundation دانشگاه آدلاید** و هم به **Foundation فلیندرز** درخواست بده. دو پیشنهادِ مسیرِ تضمینی یعنی قدرت انتخاب و پرونده‌ی ویزای قوی‌تر. پیشنهادها مشروط‌اند و نگه‌داشتنشان تا قبول‌کردن هیچ هزینه‌ای ندارد.

- [Flinders International Foundation Studies](https://www.flinders.edu.au/international/academy/courses/foundation-studies)`,
      ),

      /* ---- Module 4 ---- */
      page(
        'ماژول ۴ — دانشگاه تورنز',
        'دانشگاه',
        `# دانشگاه تورنز — گزینه‌ی منعطف و بدون ATAR

تورنز یک دانشگاه خصوصی با پردیس آدلاید است که عمداً ورودِ **منعطف و بدون ATAR** دارد. به‌عنوان پشتیبان و برای رشته‌های خلاق/طراحی/بازرگانی/سلامت مفید است:

- **مسیرهای بدون ATAR:** مدرک اخیر سال دوازدهم، یا **نمونه‌کار / مصاحبه** (برای رشته‌های خلاق و طراحی)، یا **آزمون آمادگی** (نمره‌ی ۷۰٪ به بالا برای ورود به دیپلم — بدون نیاز به مدرک دوازدهم).
- **دیپلم ← کارشناسی:** دیپلم‌ها به سال دوم کارشناسیِ متناظر وصل می‌شوند.
- **رشته‌ها:** طراحی و فناوری خلاق، بازرگانی و هتلداری، سلامت، آموزش، فناوری.

تورنز اعتبار پژوهشیِ آدلاید یا فلیندرز را ندارد، اما برای دانشجویی که نگران نمرات است استرس ATAR را کاملاً حذف می‌کند و ورود مبتنی بر مصاحبه/نمونه‌کار دارد که می‌تواند مناسب یک دانشجوی بااعتمادبه‌نفس و بیانگر باشد.

> 💡 **نکته:** تورنز «نقشه‌ی B» است — در اپ به‌عنوان یک اطمینان‌خاطر نگهش دار، نه انتخاب اول. ارزشش روانی است: ثابت می‌کند که *همیشه* یک در باز است و همین از فشار کم می‌کند.

- [تورنز — ورود بین‌المللی](https://www.torrens.edu.au/apply/international-students)`,
      ),

      /* ---- Module 5 ---- */
      page(
        'ماژول ۵ — TAFE SA و مسیرهای VET',
        'مسیر',
        `# TAFE SA و مسیرهای فنی‌وحرفه‌ای (VET)

TAFE SA کالج دولتی فنی‌وحرفه‌ای استرالیای جنوبی است و آشکارا دانشجوی بین‌المللی می‌پذیرد. دو ویژگی آن را برای آلا قدرتمند می‌کند:

**۱. پیشنهادهای بسته‌ای دانشگاه (University Packaged Offers):** یک‌بار درخواست می‌دهی و **یک ویزای دانشجویی واحد** می‌گیری که هم دیپلم TAFE و هم مدرک دانشگاهیِ مرتبط را پوشش می‌دهد. ورود تضمینی به دانشگاه در آن گنجانده شده. نمونه‌ها (۲۰۲۶):

| مدرک TAFE SA | مدرک بسته‌شده | دانشگاه |
|---|---|---|
| Diploma of Nursing | Bachelor of Nursing | Adelaide University |
| Diploma of Early Childhood Education & Care | Bachelor of Teaching (Early Childhood) | Adelaide / Flinders |
| Diploma of Marketing & Communication | Bachelor of Business (Marketing) | Adelaide University |
| دیپلم‌های IT (امنیت سایبری، کلاد، برنامه‌نویسی، شبکه…) | کارشناسی‌های IT | Adelaide University |
| Associate Degree مهندسی (عمران، مکانیک، برق، پزشکی…) | Bachelor of Engineering (Honours) | Adelaide / Flinders |
| Adv. Diploma of Screen & Media | Bachelor of Visual Effects | Adelaide University |

**۲. انتقال واحد (Credit transfer):** حتی بدون بسته، دیپلم کامل‌شده‌ی TAFE معادل بخشی از مدرک دانشگاهی شناخته می‌شود و با «advanced standing» وارد دانشگاه می‌شوی.

**منطق هزینه:** Associate Degree (۲ سال ≈ ۴۰٬۲۷۰ دلار) + ۲ سال دانشگاه ≈ **۱۴۴٬۸۷۰ دلار** در برابر **۲۰۹٬۲۰۰ دلار** برای مدرک مستقیم ۴ ساله — یعنی حدود **۶۴٬۰۰۰ دلار** صرفه‌جویی. بورسیه‌ها (Adelaide Emerging Leaders ۲۵٪ / Merit ۱۵٪) هم روی آن اعمال می‌شوند.

> 💡 **نکته:** اگر پول بزرگ‌ترین نگرانی است، **پیشنهاد بسته‌ای TAFE** اغلب ارزان‌ترین راهِ قانونی به یک مدرک آدلاید است — و یک ویزا کل مسیر را پوشش می‌دهد.

- [TAFE SA — برنامه‌های بسته‌ای دانشگاه](https://www.tafesa.edu.au/international/university-pathway-programs)`,
      ),

      /* ---- Module 6 ---- */
      page(
        'ماژول ۶ — گسترش فراتر از آدلاید',
        'مسیر',
        `# گسترش به بیرون — فراتر از آدلاید

آدلاید را پایگاه اصلی نگه دار، اما هر ایالتِ سرزمین اصلیِ استرالیا همان سیستم سه‌مسیره را دارد. اگر آلا روزی به گزینه‌ی بیشتری نیاز داشت، این‌ها معادل‌ها هستند:

| ایالت / شهر | دانشگاه‌های دولتی با Foundation/مسیر آسان | منطقه‌ای برای مهاجرت؟ |
|---|---|---|
| **SA — آدلاید** ⭐ | Adelaide University (Kaplan)، Flinders، Torrens | **بله** (کل شهر) |
| **WA — پرت** | Curtin، ECU، Murdoch، UWA (همه از طریق کالج) | **بله** (کل شهر) |
| **QLD — گلد‌کوست / مناطق** | Griffith، Southern Cross (مسیرهای TAFE قوی) | **بله** بیرون بریزبن |
| **VIC — ملبورن** | Deakin، La Trobe، Victoria U، Monash College | خیر (ملبورن = شهر بزرگ) |
| **NSW — سیدنی** | UTS، Western Sydney، Macquarie (از طریق کالج) | خیر (سیدنی = شهر بزرگ) |
| **ACT / TAS / NT** | ANU/Canberra، UTAS (تاسمانی)، CDU (داروین) | **بله** (همه منطقه‌ای) |

الگو همه‌جا یکی است: **Foundation یا دیپلم ← ورود تضمینی به کارشناسی، شروع با آیلتس حدود ۵٫۵.** فقط اسم‌ها فرق می‌کنند.

> 💡 **نکته:** به‌خاطر مهاجرت و هزینه، **شهرهای منطقه‌ای (آدلاید، پرت، گلد‌کوست، هوبارت، کانبرا) برای یک دانشجوی بین‌المللیِ کم‌بودجه بهتر از سیدنی/ملبورن‌اند.** آدلاید برای آلا اولویت اول می‌ماند.`,
      ),

      /* ---- Module 7 ---- */
      page(
        'ماژول ۷ — تحصیل مرتبط با موسیقی',
        'موسیقی',
        `# تحصیل مرتبط با موسیقی — استفاده از صدایش

آلا آواز می‌خواند و عاشق موسیقی است — و آدلاید میزبان **Elder Conservatorium of Music** است، یکی از قدیمی‌ترین کنسرواتوارهای استرالیا که حالا بخشی از دانشگاه آدلاید است. سه راه صادقانه برای گنجاندن موسیقی در برنامه‌اش:

**گزینه A — موسیقی به‌عنوان رشته‌ی اصلی.** کارشناسی موسیقی (گرایش‌ها: اجرای کلاسیک، آموزش موسیقی، اجرای خلاق؛ آواز یک رشته‌ی اصلیِ معتبر است).

| کارشناسی موسیقی — بین‌المللی | جزئیات |
|---|---|
| ورود تحصیلی | دیپلم دوازدهم/معادل SACE، یا Cert IV، یا ۶ ماه از یک دیپلم |
| انگلیسی | آیلتس **۶٫۵** (هیچ بخش زیر ۶٫۰) — *بالاتر از Foundation* |
| **آزمون ورودی (Audition)** | اجباری. نوامبر برگزار می‌شود؛ **متقاضیان خارج از کشور می‌توانند اجرای ضبط‌شده بفرستند**. |
| شهریه | حدود **۴۵٬۶۰۰ دلار در سال** (۲۰۲۶) |
| شروع | فوریه و جولای |

**گزینه B — ترکیب.** یک مدرک دوگانه مثل **کارشناسی هنر + کارشناسی موسیقی**، یا گرفتن موسیقی به‌عنوان واحد فرعی کنار رشته‌ی علوم. بهترینِ هر دو دنیا.

**گزینه C — موسیقی به‌عنوان زندگی، علوم به‌عنوان شغل.** مسیر علوم را برو (Foundation ← رشته‌ی علوم) و آواز را در گروه‌های کر، ارکسترها و برنامه‌های جامعه‌ی کنسرواتوار ادامه بده.

> 💡 **نکته‌ی صادقانه:** کارشناسی موسیقی به **آیلتس ۶٫۵ + audition** نیاز دارد — سختگیرانه‌تر از مسیر علوم‌+Foundation. اگر موسیقی *شغلِ رؤیایی* اوست، از همین حالا آماده‌سازی audition و آیلتس را شروع کند. اگر موسیقی *شادیِ* اوست و علوم *برنامه‌اش*، گزینه‌ی C هر دو را بدون فشار اضافه حفظ می‌کند.

- [کارشناسی موسیقی — کنسرواتوار الدر](https://able.adelaide.edu.au/music/bachelor-of-music)
- [ثبت‌نام و دورهای آزمون](https://adelaide.edu.au/about/school/elder-conservatorium-of-music-and-school-of-performing-arts/auditions/)
- [الزامات آزمون بر اساس رشته](https://able.adelaide.edu.au/music/study/auditions/audition-requirements-by-program)`,
      ),

      /* ---- Module 8 ---- */
      page(
        'ماژول ۸ — انگلیسی: تنها نمره‌ای که همه‌چیز را باز می‌کند',
        'آزمون',
        `# انگلیسی: تنها نمره‌ای که همه‌چیز را باز می‌کند

انگلیسی تنها عددی است که آلا امسال کاملاً کنترلش می‌کند. هم درِ دوره را باز می‌کند و هم درِ ویزا را. اهداف:

| هدف | آیلتس آکادمیک (تقریبی) |
|---|---|
| ورود Foundation Studies | **۵٫۵** (هیچ بخش زیر ۵٫۰) |
| ورود دیپلم / degree-transfer | **۵٫۵** (بخش‌ها ۵٫۰ به بالا) |
| ورود مستقیم کارشناسی (عمومی) | **۶٫۰** (هیچ بخش زیر ۵٫۵) |
| کارشناسی موسیقی | **۶٫۵** (هیچ بخش زیر ۶٫۰) |
| کف انگلیسیِ ویزای دانشجویی | **۶٫۰** (یا معادل پذیرفته‌شده) |

**آزمون‌های پذیرفته‌شده:** IELTS، **PTE Academic**، TOEFL iBT و اغلب Duolingo/LanguageCert/OET. آزمون PTE کامپیوتری، سریع است و رزروش در کشورهای حاشیه‌ی خلیج فارس اغلب راحت‌تر است — مقایسه‌اش ارزش دارد.

**مسیرهای بدون آزمون:** اگر نمره‌اش نزدیک باشد، کالج‌ها یک **پیشنهاد مشروط + دوره‌ی آماده‌سازی انگلیسی (ELICOS)** را پیش از برنامه‌ی اصلی بسته می‌کنند؛ او شروع می‌کند، انگلیسی را همان‌جا بهتر می‌کند و بعد وارد Foundation می‌شود.

> 💡 **نکته:** همین ماه یک **آزمون آزمایشی رایگان آنلاین** هم برای IELTS و هم PTE بده تا نمره‌ی واقعیِ شروعش را ببیند، بعد ۸ تا ۱۲ هفته برنامه‌ی آماده‌سازی بچیند. بهبود انگلیسی پرتأثیرترین کاری است که می‌تواند برای ۲۰۲۷ انجام دهد — خیلی بیشتر از نگرانی بابت نمرات گذشته.

- [IELTS](https://ielts.org)
- [PTE Academic](https://www.pearsonpte.com)
- [مسیر انگلیسی بدون آزمون (فلیندرز)](https://www.flinders.edu.au/international/apply/english-language-pathways)`,
      ),

      /* ---- Module 9 ---- */
      page(
        'ماژول ۹ — ویزای دانشجویی (Subclass 500)',
        'ویزا',
        `# ویزای دانشجویی (Subclass 500)

وقتی کالج **CoE** صادر کرد، آلا آنلاین برای ویزای دانشجویی **Subclass 500** درخواست می‌دهد. شرایط اصلی (تا ژوئیه‌ی ۲۰۲۶):

| شرط | جزئیات (تقریبی — رقم روز را چک کن) |
|---|---|
| **توان مالی** | اثبات حدود **۲۹٬۷۱۰ دلار** هزینه‌ی زندگی سال اول، **به‌علاوه‌ی** شهریه، **به‌علاوه‌ی** بلیت رفت‌وبرگشت |
| **هزینه‌ی درخواست ویزا** | **۲٬۰۰۰ دلار** هزینه‌ی پایه (از ۱ ژوئیه‌ی ۲۰۲۵؛ پیش از ثبت چک کن) |
| **انگلیسی** | آیلتس **۶٫۰** یا معادل پذیرفته‌شده |
| **Genuine Student (GS)** | متن‌های کوتاه (حدود ۱۵۰ کلمه هرکدام) درباره‌ی چرایی این رشته، چرا استرالیا، و برنامه‌ی شغلی در وطن |
| **OSHC** | بیمه‌ی سلامت برای **کل** مدت ویزا (حدود ۶۰۰ تا ۱٬۰۰۰ دلار در سال) |
| **سلامت و پیشینه** | معاینه‌ی پزشکی + گواهی عدم سوءپیشینه در صورت نیاز |

متن‌های **Genuine Student** جایی است که داستان آلا به نقطه‌ی قوت تبدیل می‌شود: روایتِ روشنِ «Foundation علوم ← رشته‌ی پرستاری/علوم در آدلاید ← بازگشت با مهارت» صادقانه خوانده می‌شود. برنامه‌ی مبهم پرخطر به‌نظر می‌رسد.

> ⚠️ **مهم:** قوانین مدارک مالی سختگیرانه‌اند و پرونده‌ی ایران با دقت بررسی می‌شود. **پولِ واقعی، مستند و باثبات** نشان بده (ماژول ۱۰). هرگز از «پولِ قرضیِ یک‌روزه» برای نمایش استفاده نکن — افسر ویزا سابقه‌ی حساب را چک می‌کند.

- [ویزای دانشجویی (Subclass 500)](https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/student-500)`,
      ),

      /* ---- Module 10 ---- */
      page(
        'ماژول ۱۰ — واقعیت‌های ویژه‌ی ایران',
        'ویزا',
        `# واقعیت‌های ویژه‌ی ایران: پول، بانک، تحریم‌ها

این ماژولی است که بیشتر راهنماها از آن می‌گذرند، اما همان چیزی است که برای یک خانواده‌ی ایرانی واقعاً تعیین‌کننده است.

- **هیچ قانون استرالیایی مانع دسترسی دانشجویان ایرانی به پولشان در ایران نیست** — مگر جایی که یک بانک ایرانیِ مشخص تحت تحریم باشد. راه‌حل عملی است: **انتقال پول از طریق یک بانک غیرتحریمی.**
- **انتقال‌های ۲۰٬۰۰۰ دلار استرالیا یا بیشتر** بین استرالیا و ایران به **تأیید DFAT (وزارت امور خارجه)** نیاز دارد؛ مبالغ **کمتر** از این آستانه نیاز ندارد. پرداخت‌ها را بر همین اساس برنامه‌ریزی کن.
- **پرداخت شهریه از داخل ایران سخت‌ترین مرحله‌ی کل مسیر است.** راه‌های رایجی که خانواده‌ها استفاده می‌کنند: پرداخت از طریق حساب یا خویشاوندی **خارج از ایران** (مثلاً در عمان، امارات، ترکیه)، استفاده از ارائه‌دهندگان پرداخت بین‌المللیِ موردتأیید دانشگاه، یا آژانس‌های تحصیلی که انتقال‌های منطبق با قوانین را انجام می‌دهند. **سه سال زندگی خانواده در عمان** اینجا یک مزیت واقعی است — یک حساب بانکی عمانی هم پرداخت شهریه و هم اثبات مالی را ساده می‌کند.
- **اگر وسط دوره پرداخت سخت شد**، مؤسسه‌ها می‌توانند طبق قانون ملی (استاندارد ۹) برای شرایط خارج از کنترل دانشجو **طرح پرداخت، تعویق (deferral) یا کاهش بار درسی** بدهند. زود با دفتر بین‌الملل صحبت کن — سکوت نکن.
- **ممنوعیت کلیِ رشته‌ای** در راهنمای دولتیِ ویژه‌ی ایرانیان دیده نمی‌شود. مسیر پرستاری/سلامت/علوم عمومی/موسیقی هیچ‌کدام از این حساسیت‌ها را ندارد.

> 💡 **نکته‌ی طلایی:** از **پایگاه خانواده در عمان** آگاهانه استفاده کن. یک حساب بانکیِ عمانی (یا کشور دیگری در خلیج فارس) برای پرداخت شهریه و برای مدارک مالیِ ویزا، بزرگ‌ترین اصطکاکی را که متقاضیان ایرانی با آن روبه‌رو می‌شوند برمی‌دارد. پیش از پرداخت هر پولی، با دفتر بین‌المللِ کالجِ انتخابی درباره‌ی *کانال‌های پرداخت موردتأیید* صحبت کن.

- [دولت استرالیا — اطلاعات برای دانشجویان ایرانی](https://internationaleducation.gov.au/international-network/australia/policyupdates/Pages/Article-InformationforIranianstudents.aspx)`,
      ),

      /* ---- Module 11 ---- */
      page(
        'ماژول ۱۱ — هزینه‌ها و بورسیه‌ها',
        'بودجه',
        `# هزینه‌ها و بورسیه‌ها — نقشه‌ی پول

بازه‌ی بودجه‌ی واقع‌بینانه برای آدلاید (سالانه، تقریبی):

| مورد | هزینه‌ی سالانه‌ی تقریبی (دلار استرالیا) |
|---|---|
| شهریه‌ی سال Foundation | ~۳۰٬۰۰۰ تا ۳۳٬۰۰۰ |
| شهریه‌ی کارشناسی (علوم/هنر) | ~۳۸٬۰۰۰ تا ۴۸٬۰۰۰ |
| شهریه‌ی کارشناسی موسیقی | ~۴۵٬۶۰۰ |
| هزینه‌ی زندگی (حداقل ویزا) | ~۲۹٬۷۱۰ |
| بیمه‌ی سلامت OSHC | ~۶۰۰ تا ۱٬۰۰۰ |
| آدلاید در برابر سیدنی/ملبورن | اجاره و زندگی **به‌طور محسوسی ارزان‌تر** |

**بورسیه‌هایی که آلا واقع‌بینانه می‌تواند بگیرد:**

| بورسیه | ارزش | توضیح |
|---|---|---|
| بورسیه‌ی خودکار Foundation فلیندرز | **۲۰٪** تخفیف Foundation | خودکار؛ به‌علاوه‌ی تا ۳۰٪ تخفیف کارشناسی بعدی بر اساس نتایج |
| Adelaide Merit Scholarship | **۱۵٪** تخفیف شهریه | بررسی خودکار؛ نیاز به معادلِ ATAR ~۸۰ / معدل ۴٫۵ — که **بعد از** یک سال Foundation خوب دست‌یافتنی است |
| Adelaide Global Citizens Scholarship | **۱۵ تا ۳۰٪** | بر اساس شایستگی، بررسی خودکار |
| TAFE SA Emerging Leaders / Merit | **۲۵٪ / ۱۵٪** | روی پیشنهادهای بسته‌ای دانشگاه |

> 💡 **نکته:** یک **نتیجه‌ی خوبِ سال Foundation کارنامه‌ی تحصیلی آلا را از نو تنظیم می‌کند.** بورسیه‌های شایستگی به معدلِ *Foundation/دیپلم* نگاه می‌کنند، نه نمرات دوازدهم — پس یک سال Foundation خوب می‌تواند عملاً یک تخفیف ۱۵ تا ۳۰ درصدیِ شهریه برایش بیاورد.

- [Adelaide Merit Scholarship (۱۵٪)](https://adelaide.edu.au/study/scholarships/int/adelaide-merit-scholarship-15/)`,
      ),

      /* ---- Module 12 ---- */
      page(
        'ماژول ۱۲ — مزیت پنهان آدلاید',
        'مهاجرت',
        `# مزیت پنهان آدلاید: ماندن و کار پس از فارغ‌التحصیلی

آدلاید یک پایتخت کامل است اما از نظر مهاجرتی **«منطقه‌ای» (regional)** طبقه‌بندی می‌شود — مزیتی واقعی که بیشتر دانشجوها نمی‌دانند:

- **کار بیشتر پس از تحصیل.** ویزای فارغ‌التحصیلی موقت (485) به فارغ‌التحصیلان آدلاید **یک سال بیشتر** نسبت به سیدنی/ملبورن می‌دهد — مثلاً حدود **۳ سال** بعد از کارشناسی به‌جای ۲ (و امکان تمدید منطقه‌ای بیشتر).
- **نامزدی ایالتی.** استرالیای جنوبی فهرست گسترده‌ای از مشاغل ماهر (حدود ۵۰۰ شغل) دارد و **امتیاز اضافه** برای ویزاهای مهارتی (Subclass 190 / 491) به‌علاوه‌ی تخفیف‌های ویژه‌ی فارغ‌التحصیلان می‌دهد.
- **امتیاز مهاجرتی.** تحصیل + زندگی در یک منطقه‌ی regional امتیاز مسیرهای اقامت دائم را بالا می‌برد.

برای خانواده‌ای که روی یک مدرک سرمایه‌گذاری می‌کند، این یعنی تحصیل به آینده‌ی واقع‌بینانه‌ی **«بمان، کار کن، بعد تصمیم بگیر»** تبدیل می‌شود — بدون تعهد به چیزی از همین حالا.

- [StudyAdelaide — مزایای مهاجرتی منطقه‌ای](https://studyadelaide.com/work/migration-opportunities)`,
      ),

      /* ---- Module 13 ---- */
      page(
        'ماژول ۱۳ — برنامه‌ی عملیاتی ۲۰۲۷',
        'برنامه',
        `# برنامه‌ی عملیاتی ۲۰۲۷ — ماه‌به‌ماه

الان اواسط ۲۰۲۶ است. این یک جدول زمانیِ واقع‌بینانه برای شروع تحصیل در استرالیا در **۲۰۲۷** است.

| کِی | این کار را بکن |
|---|---|
| **الان – اوت ۲۰۲۶** | ① دوازدهم را تمام کن. ② یک آزمون آزمایشی رایگان IELTS/PTE بده تا سطح شروعش را بفهمد. ③ سه مسیر را کوتاه‌فهرست کن: **Adelaide Foundation، Flinders Foundation، یک پیشنهاد بسته‌ای TAFE.** |
| **اوت – اکتبر ۲۰۲۶** | ④ آماده‌سازی منظم IELTS/PTE را شروع کن (هدف ۵٫۵ تا ۶٫۰). ⑤ مدارک را جمع کن (ریزنمرات، پاسپورت، نتایج یازدهم/دوازدهم). ⑥ با دفاتر بین‌الملل تماس بگیر؛ درباره‌ی *کانال‌های پرداخت ویژه‌ی ایرانیان* و *پیشنهادهای مشروط همراه با دوره‌ی انگلیسی* بپرس. |
| **اکتبر – دسامبر ۲۰۲۶** | ⑦ درخواست‌های Foundation/مسیر را ارسال کن (نگه‌داشتنشان رایگان است). ⑧ در آزمون IELTS/PTE شرکت کن. ⑨ پیشنهادهای مشروط را بگیر. |
| **دسامبر ۲۰۲۶ – ژانویه ۲۰۲۷** | ⑩ شرط‌ها (انگلیسی) را برآورده کن، یک پیشنهاد را قبول کن، پیش‌پرداخت بده ← **CoE** بگیر. ⑪ OSHC و مدارک مالی را ترتیب بده (از حساب عمان استفاده کن). |
| **ژانویه – فوریه ۲۰۲۷** | ⑫ ویزای **Subclass 500** را با متن‌های Genuine Student ثبت کن. ⑬ پس از صدور، بلیت و اقامت رزرو کن. |
| **فوریه ۲۰۲۷** | 🎓 **شروع Foundation Studies در آدلاید** ← ورود تضمینی به کارشناسی در ۲۰۲۸. |

*نسخه‌ی سریع‌تر:* اگر انگلیسی‌اش همین حالا حدود ۵٫۵ باشد، یک **Foundation ۸ ماهه با شروع اواسط ۲۰۲۷** یا یک **دیپلم مستقیم** می‌تواند این مسیر را فشرده‌تر کند. *نسخه‌ی موسیقی:* از اوت ۲۰۲۶ آماده‌سازیِ ضبط audition را اضافه کن و هدف را آیلتس ۶٫۵ بگذار.

> 💡 **نکته:** درخواست‌ها و پیشنهادها برای نگه‌داشتن هیچ هزینه‌ای ندارند. **زود و به بیش از یک مسیر درخواست بده.**`,
      ),

      /* ---- Module 14 ---- */
      page(
        'ماژول ۱۴ — فهرست مدارک',
        'مدارک',
        `# فهرست مدارک

- [ ] پاسپورت (معتبر تا مدتی پس از ۲۰۲۷)
- [ ] کارنامه‌ی سال یازدهم + مدرک پایان دوازدهم
- [ ] ترجمه‌ی رسمیِ انگلیسیِ همه‌ی کارنامه‌ها
- [ ] نتیجه‌ی IELTS/PTE
- [ ] مدارک مالی (صورت‌حساب بانکی — ترجیحاً حساب عمان با سابقه‌ی باثبات)
- [ ] انگیزه‌نامه / پاسخ‌های Genuine Student (زود پیش‌نویس کن)
- [ ] عکس پاسپورتی
- [ ] بیمه‌نامه‌ی OSHC (در مرحله‌ی CoE ترتیب داده می‌شود)
- [ ] رزومه (برای بعضی درخواست‌های مسیر و بورسیه مفید است)

> ⚠️ **نکته‌ی به‌روزبودن:** شهریه‌ها، مبالغ ویزا، نمرات ورودی و تاریخ‌های شروع مرتب تغییر می‌کنند. هر عددِ این راهنما تا **ژوئیه‌ی ۲۰۲۶** درست بوده. پیش از اینکه آلا *درخواست بدهد یا پول پرداخت کند*، رقم دقیق را در صفحه‌ی رسمی دوباره چک کنید.`,
      ),

      /* ---- Module 15 ---- */
      page(
        'ماژول ۱۵ — یادداشتی برای آلا',
        'شخصی',
        `# یادداشتی برای آلا — تو از پسش برمی‌آیی 🌱

آلا — نمرات پایین یک فصل سخت‌اند، نه کل داستان. سیستم استرالیا با دانشجویانی که یک سال زمین خوردند به‌طور غیرمعمولی مهربان است: می‌گذارد سالِ *بعدیِ* تحصیل تو، نه بدترین سالت، تعریفت کند. یک سال Foundation قوی حتی می‌تواند برایت بورسیه‌ای بیاورد که نمرات دوازدهم هرگز نمی‌آورد.

تو همین حالا سه مزیت آرام داری:
- رشته‌ی علوم
- پایگاه خانواده در عمان که سخت‌ترین مشکل مالی را حل می‌کند
- صدایی که همیشه در گروه‌های کر و صحنه‌های آدلاید جا دارد

از این راهنما یک مسیر انتخاب کن، همین ماه یک آزمون آزمایشی انگلیسی بده، و بگذار برنامه بقیه‌اش را پیش ببرد. قدم‌به‌قدم. 🌱

---

## منابع
*همه‌ی ارقام در ژوئیه‌ی ۲۰۲۶ راستی‌آزمایی شده‌اند. پیش از درخواست یا پرداخت، صفحات رسمی را دوباره چک کنید.*

- [شرایط ورود بین‌المللی آدلاید](https://adelaide.edu.au/study/international-students/how-to-apply/entry-requirements/)
- [Kaplan KICA — Foundation](https://www.kaplancollegeadelaide.edu.au/apply/entry-requirements)
- [Flinders Foundation بین‌المللی](https://www.flinders.edu.au/international/academy/courses/foundation-studies)
- [تورنز — ورود بین‌المللی](https://www.torrens.edu.au/apply/international-students)
- [TAFE SA — برنامه‌های بسته‌ای](https://www.tafesa.edu.au/international/university-pathway-programs)
- [کارشناسی موسیقی — کنسرواتوار الدر](https://adelaide.edu.au/study/degrees/bachelor-of-music-honours-classical-performance/)
- [ویزای دانشجویی (Subclass 500)](https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/student-500)
- [دولت استرالیا — اطلاعات برای دانشجویان ایرانی](https://internationaleducation.gov.au/international-network/australia/policyupdates/Pages/Article-InformationforIranianstudents.aspx)
- [Adelaide Merit Scholarship (۱۵٪)](https://adelaide.edu.au/study/scholarships/int/adelaide-merit-scholarship-15/)
- [StudyAdelaide — مزایای مهاجرتی منطقه‌ای](https://studyadelaide.com/work/migration-opportunities)`,
      ),
    ]
  }

  /* ================================================================ ENGLISH */
  return [
    /* ---- Module 0 ---- */
    page(
      'Module 0 — The Honest Strategy',
      'pathway',
      `# Start Here: The Honest Strategy

Ala, here is the most important thing to understand first: **below-average Year 12 marks do not close the door to an Australian university.** Australia was built for exactly your situation. There are three legal, well-worn routes into a degree, and two of them barely look at your final Year 12 grades at all:

1. **Foundation Studies** — a 8–12 month university preparation year. Entry needs only **Year 11 completed with ~50%** and basic English. Finish it and you get *guaranteed* entry into a bachelor degree. This is the single most powerful tool for a student with weak final-year marks.
2. **Diploma / TAFE pathway** — a 1–2 year vocational or college diploma that (a) is a real qualification on its own and (b) transfers as credit into year 1 or year 2 of a degree. Entry is forgiving.
3. **Direct entry** — straight into a bachelor degree. This is the only route where marks matter a lot, so for now we treat it as "plan C," not the goal.

The low marks feel enormous right now. In the Australian pathway system they are a detour, not a wall. Your job for the next 12 months is not to fix the past — it's to build the strongest *foundation or diploma application* and a strong *English score*. Those two things, not your Year 12 average, decide 2027.

> 💡 **Tip:** Complete Year 12 anyway. Even an average Year 12 completion certificate makes foundation and diploma applications smoother and keeps *direct entry* alive as a backup. A completed Year 12 > an incomplete one, always.`,
    ),

    /* ---- Module 1 ---- */
    page(
      'Module 1 — Australian System Glossary',
      'pathway',
      `# How the Australian System Works — Glossary

Five words unlock almost everything. Learn these and the rest of the guide reads easily.

| Term | Plain meaning |
|---|---|
| **ATAR** | The rank (0–99.95) Australian schools use for Year 12. As an international student Ala usually does **not** need an ATAR — her country's Year 12 is assessed on its own equivalency table. |
| **Foundation Studies** | A bridging year that replaces the need for strong final marks. Ends in guaranteed degree entry. |
| **Diploma / TAFE / VET** | Vocational qualifications (TAFE = the public college network; VET = the whole vocational sector). Can stand alone *or* feed into a degree with credit. |
| **CRICOS** | The official register of courses approved to enrol international students. If a course has a CRICOS code, it's visa-eligible. Always check this. |
| **CoE** | *Confirmation of Enrolment* — the document the college issues after you accept + pay a deposit. You cannot lodge a student visa without it. |
| **Genuine Student (GS)** | The short written statements in the visa application explaining why you're studying. Replaced the old "GTE" in 2024. |
| **OSHC** | *Overseas Student Health Cover* — compulsory private health insurance for the whole visa. |

> 💡 **Tip:** When you look at any course, find its **CRICOS code** and its **English requirement** first. Those two lines tell you instantly if it's realistic for you.`,
    ),

    /* ---- Module 2 ---- */
    page(
      'Module 2 — Adelaide University',
      'university',
      `# Adelaide First: Adelaide University

Big 2026 change to know: **the University of Adelaide and the University of South Australia (UniSA) merged into one institution — "Adelaide University" — which opened in January 2026.** So when you see old pages for "UniSA" or "University of Adelaide," they now point to the same new university. Its official pathway college is **Kaplan International College Adelaide** (formerly "The University of Adelaide College").

This matters for Ala because Adelaide University now runs **both** the research-university degrees *and* a full ladder of pathway programs under one roof, in a city that is cheaper and calmer than Sydney or Melbourne — and (see Module 12) counts as **regional** for migration.

**Two realistic doors into Adelaide University:**

| Door | What it is | Entry (indicative) | English (indicative) | Leads to |
|---|---|---|---|---|
| **Foundation Studies** (via Kaplan College) | 8–12 month uni-prep year | Year 11 pass ~50%, *or* Year 12 | IELTS **5.5** (no band under 5.0) | Guaranteed 1st year of most Adelaide bachelor degrees |
| **Degree Transfer / Diploma** (via Kaplan College) | 1st-year-equivalent diploma | ATAR ~45–50 or Foundation 40–50% | IELTS **5.5** (bands 5.0+) | Direct into **2nd year** of the degree |
| **Direct entry** | Straight into the bachelor | Competitive Year 12 (country table) | IELTS **6.0** (degree-specific, higher for some) | Year 1 of the degree |

> 💡 **Tip:** For Ala, the **Foundation Studies** door at Adelaide is the headline recommendation. It ignores weak Year 12 marks, needs only IELTS 5.5, and ends in a guaranteed degree place. If her English is already stronger, the **Degree Transfer diploma** saves a whole year by dropping her into 2nd year.

- [Adelaide University international entry requirements](https://adelaide.edu.au/study/international-students/how-to-apply/entry-requirements/)
- [Kaplan KICA — Foundation Studies](https://college.adelaide.edu.au/programs/foundation-studies/)`,
    ),

    /* ---- Module 3 ---- */
    page(
      "Module 3 — Flinders University (Adelaide's second option)",
      'university',
      `# Flinders University — Adelaide's Second Option

Flinders is the other major public university in Adelaide, and it did **not** merge — it stands alone with its own, very accessible pathway. Its **International Foundation Studies Program** is a clean fit for Ala's profile:

| Feature | Detail |
|---|---|
| Entry (academic) | Completed **Year 11** or equivalent; min age 17 |
| English | IELTS **5.5** (no band under 5.0) |
| Length & intakes | **12-month** (starts Feb) or **8-month** (starts June & Oct) |
| Tuition | ~**AUD $30,500** for the foundation year |
| Leads to | **Guaranteed entry** to 50+ Flinders bachelor degrees (incl. Science, Health, Nursing, Business, Engineering) |
| Scholarship | **Automatic 20%** off the foundation program; up to **30%** off the later bachelor on performance |
| Direct-entry English | IELTS **6.0** (UG) for those who skip foundation |

Because Ala's stream is **science**, Flinders is worth shortlisting: it is strong in health sciences and nursing, and the foundation program has a Science + Maths learning area she can lean into.

> 💡 **Tip:** Apply to **both** Adelaide University Foundation *and* Flinders Foundation. Two guaranteed-pathway offers = leverage, choice, and a stronger visa case. Foundation offers are conditional and cost nothing to hold until you accept.

- [Flinders International Foundation Studies](https://www.flinders.edu.au/international/academy/courses/foundation-studies)`,
    ),

    /* ---- Module 4 ---- */
    page(
      'Module 4 — Torrens University (flexible, no-ATAR)',
      'university',
      `# Torrens University — The Flexible, No-ATAR Option

Torrens is a private university with an Adelaide campus and deliberately **flexible, no-ATAR entry**. It's useful as a backup and for creative/design/business/health fields:

- **No-ATAR pathways:** recent Year 12 certificate, a **portfolio or interview** (for creative/design courses), or a **readiness test** (score 70%+ to enter a diploma — *no Year 12 certificate required*).
- **Diploma → degree:** diplomas feed into year 2 of the matching bachelor.
- **Fields:** Design & Creative Technology, Business & Hospitality, Health, Education, Technology.

Torrens won't carry the same research prestige as Adelaide or Flinders, but for a student worried about marks it removes the ATAR anxiety entirely and offers interview/portfolio-based entry — which can suit a confident, expressive student.

> 💡 **Tip:** Torrens is "plan B" — keep it in the app as reassurance, not the first choice. The value is psychological: it proves there is *always* a door open, which lowers the pressure.

- [Torrens University — international entry](https://www.torrens.edu.au/apply/international-students)`,
    ),

    /* ---- Module 5 ---- */
    page(
      'Module 5 — TAFE SA & VET Pathways',
      'pathway',
      `# TAFE SA & Vocational (VET) Pathways

TAFE SA is South Australia's public vocational college, and it openly enrols international students. Two features make it powerful for Ala:

**1. University Packaged Offers** — you apply once and get a **single student visa** covering *both* the TAFE diploma **and** the linked university degree. Guaranteed university entry is built in. Examples (2026):

| TAFE SA qualification | Packaged degree | University |
|---|---|---|
| Diploma of Nursing | Bachelor of Nursing | Adelaide University |
| Diploma of Early Childhood Education & Care | Bachelor of Teaching (Early Childhood) | Adelaide / Flinders |
| Diploma of Marketing & Communication | Bachelor of Business (Marketing) | Adelaide University |
| IT diplomas (Cyber Security, Cloud, Programming, Networking…) | IT bachelor degrees | Adelaide University |
| Associate Degrees in Engineering (Civil, Mechanical, Electrical, Biomedical…) | Bachelor of Engineering (Honours) | Adelaide / Flinders |
| Adv. Diploma of Screen & Media | Bachelor of Visual Effects | Adelaide University |

**2. Credit transfer** — even without a package, a completed TAFE diploma is recognised as equivalent to parts of a degree, so you enter university with advanced standing (skip subjects).

**Cost logic:** An Associate Degree (2 yrs ≈ AUD $40,270) + 2 years of university ≈ **AUD $144,870** total vs **AUD $209,200** for a straight 4-year degree — a saving of about **AUD $64,000**. Scholarships (Adelaide Emerging Leaders 25% / Merit 15%) can apply on top.

> 💡 **Tip:** If money is the biggest worry, the **TAFE packaged offer** is often the cheapest legitimate route to an Adelaide degree — *and* one visa covers the whole journey.

- [TAFE SA — university packaged programs](https://www.tafesa.edu.au/international/university-pathway-programs)`,
    ),

    /* ---- Module 6 ---- */
    page(
      'Module 6 — Broadening Outward (beyond Adelaide)',
      'pathway',
      `# Broadening Outward — Beyond Adelaide

Keep Adelaide as home base, but every mainland state has the same three-route system. If Ala ever needs more options, these are the equivalents:

| State / City | Public unis with easy foundation/pathway | Also "regional" for migration? |
|---|---|---|
| **SA — Adelaide** ⭐ | Adelaide University (Kaplan), Flinders, Torrens | **Yes** (whole city) |
| **WA — Perth** | Curtin, ECU, Murdoch, UWA (all via colleges) | **Yes** (whole city) |
| **QLD — Gold Coast / regional** | Griffith, Southern Cross (strong TAFE pathways) | **Yes** outside Brisbane |
| **VIC — Melbourne** | Deakin, La Trobe, Victoria U, Monash College | No (Melbourne = major city) |
| **NSW — Sydney** | UTS, Western Sydney, Macquarie (via colleges) | No (Sydney = major city) |
| **ACT / TAS / NT** | ANU/Canberra, UTAS (Tasmania), CDU (Darwin) | **Yes** (all regional) |

The pattern is identical everywhere: **Foundation or Diploma → guaranteed degree entry, IELTS ~5.5 to start.** Only the names change.

> 💡 **Tip:** For migration and cost reasons, **regional cities (Adelaide, Perth, Gold Coast, Hobart, Canberra) beat Sydney/Melbourne** for an international student on a budget. Adelaide stays #1 for Ala.`,
    ),

    /* ---- Module 7 ---- */
    page(
      'Module 7 — Music-Linked Study',
      'music',
      `# Music-Linked Study — Using Her Voice

Ala sings and loves music — Adelaide happens to host the **Elder Conservatorium of Music**, one of Australia's oldest, now part of Adelaide University. Three honest ways to fold music into her plan:

**Option A — Study music as the degree.** Bachelor of Music (majors incl. Classical Performance, Music Education, Creative Practice; voice is a valid principal study).

| Bachelor of Music — international | Detail |
|---|---|
| Academic entry | Year 12 / SACE-equivalent, *or* Cert IV, *or* 6 months of a diploma |
| English | IELTS **6.5** (no band under 6.0) — *higher than foundation* |
| **Audition** | Compulsory. Held in November; **overseas applicants may submit a recorded performance**. Non-refundable audition fee. |
| Tuition | ~**AUD $45,600/yr** (2026) |
| Intakes | February & July |

**Option B — Combine.** A double degree like **Bachelor of Arts + Bachelor of Music**, or take music as a minor/elective alongside a science degree. Best of both worlds.

**Option C — Music as life, science as career.** Do the science pathway (foundation → science degree) and keep singing through university choirs, ensembles and the Con's community programs. Nothing is lost; the audition/IELTS 6.5 pressure is removed.

> 💡 **Honest note:** The Bachelor of Music needs **IELTS 6.5 + an audition** — a higher bar than the science-foundation route. If music is her dream *career*, start audition and IELTS prep now. If music is her *joy* but science is her *plan*, Option C protects both without the extra pressure.

- [Bachelor of Music — Elder Conservatorium](https://able.adelaide.edu.au/music/bachelor-of-music)
- [Audition registration and rounds](https://adelaide.edu.au/about/school/elder-conservatorium-of-music-and-school-of-performing-arts/auditions/)
- [Audition requirements by program](https://able.adelaide.edu.au/music/study/auditions/audition-requirements-by-program)`,
    ),

    /* ---- Module 8 ---- */
    page(
      'Module 8 — English: The One Score That Unlocks Everything',
      'exam',
      `# English: The One Score That Unlocks Everything

English is the *one* number Ala fully controls this year. It gates both the course **and** the visa. Targets:

| Goal | IELTS Academic (indicative) |
|---|---|
| Foundation Studies entry | **5.5** (no band under 5.0) |
| Diploma / degree-transfer entry | **5.5** (bands 5.0+) |
| Direct bachelor entry (general) | **6.0** (no band under 5.5) |
| Bachelor of Music | **6.5** (no band under 6.0) |
| Student visa English floor | **6.0** (or an accepted equivalent) |

**Accepted tests:** IELTS, **PTE Academic**, TOEFL iBT, and often Duolingo/LanguageCert/OET. PTE is computer-marked, fast, and many students in the Gulf find it easier to book — worth comparing.

**No-test routes:** If she's close, colleges bundle a **conditional offer + English preparation course** (ELICOS) before the main program — she starts, improves English on-site, then rolls into foundation. Living/studying in Oman means her English may already be closer to target than she fears.

> 💡 **Tip:** Book a **free online practice test** for both IELTS and PTE this month to see her real starting band, then plan 8–12 weeks of prep. English improvement is the highest-leverage thing she can do for 2027 — far more than worrying about past marks.

- [IELTS](https://ielts.org)
- [PTE Academic](https://www.pearsonpte.com)
- [Test-free English routes (Flinders)](https://www.flinders.edu.au/international/apply/english-language-pathways)`,
    ),

    /* ---- Module 9 ---- */
    page(
      'Module 9 — Student Visa (Subclass 500)',
      'visa',
      `# The Student Visa (Subclass 500)

Once a college issues a **CoE**, Ala applies online for the **Subclass 500** student visa. The core requirements (as of July 2026):

| Requirement | Detail (indicative — verify current figures) |
|---|---|
| **Financial capacity** | Proof of ~**AUD $29,710** living costs for year 1, **plus** tuition, **plus** return airfare |
| **Visa application fee** | **AUD $2,000** base fee (since 1 July 2025; confirm current before lodging) |
| **English** | IELTS **6.0** or accepted equivalent |
| **Genuine Student (GS)** | Short written statements (≈150 words each) on why this course, why Australia, and career plans back home |
| **OSHC** | Health insurance for the **whole** visa (~AUD $600–1,000/yr) |
| **Health & character** | Medical exam + police clearance as required |

The **Genuine Student** statements are where Ala's story becomes an asset: a clear "science foundation → nursing/science degree in Adelaide → return with skills" narrative reads as genuine. Vague plans read as risky.

> ⚠️ **Important:** The financial-evidence rules are strict and Iran is assessed carefully. Show **genuine, well-documented, stable funds** (see Module 10). Never use borrowed-for-a-day "show money" — visa officers check the history.

- [Student Visa (subclass 500)](https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/student-500)`,
    ),

    /* ---- Module 10 ---- */
    page(
      'Module 10 — Iran-Specific Realities',
      'visa',
      `# Iran-Specific Realities: Money, Banking, Sanctions

This is the module most guides skip, and it's the one that actually decides things for an Iranian family. The Australian government publishes specific guidance for Iranian students. Key points:

- **There is no Australian law stopping Iranian students from accessing their funds in Iran** — *except* where a specific Iranian bank is under sanction. The fix is practical: **move funds through a non-sanctioned bank.**
- **Transfers of AUD $20,000 or more** between Australia and Iran need **DFAT (Foreign Affairs) approval**; amounts **under** that threshold do not. Plan payments accordingly.
- **Paying tuition from Iran is the hardest single step.** Common workarounds families use: paying via a relative or account **outside Iran** (e.g. in Oman, UAE, Türkiye), using the university's approved international payment providers, or education agents who handle compliant transfers. The family's **3 years in Oman** is a real advantage here — an Omani bank account can simplify both fee payment and financial-evidence.
- **If fees become hard mid-course**, institutions can offer **payment plans, deferrals, or reduced load** under the National Code (Standard 9) for circumstances beyond the student's control. Ask the international office early — don't go silent.
- **No blanket field-of-study ban** appears in the government's Iranian-student guidance, but some highly sensitive science/technology fields can attract extra security vetting for any applicant. A nursing/health/general-science/music path carries none of that sensitivity.

> 💡 **Tip:** Use the family's **Oman base** deliberately. An Omani (or other Gulf) bank account for fee payment and for the visa's financial evidence removes the single biggest friction Iranian applicants face. Talk to the chosen college's international office about *approved payment channels* before paying anything.

- [Australian Govt — information for Iranian students](https://internationaleducation.gov.au/international-network/australia/policyupdates/Pages/Article-InformationforIranianstudents.aspx)`,
    ),

    /* ---- Module 11 ---- */
    page(
      'Module 11 — Costs & Scholarships',
      'budget',
      `# Costs & Scholarships — The Money Map

Realistic budget ranges for Adelaide (per year, indicative):

| Item | Indicative annual cost (AUD) |
|---|---|
| Foundation year tuition | ~$30,000–$33,000 |
| Bachelor tuition (science/arts) | ~$38,000–$48,000 |
| Bachelor of Music tuition | ~$45,600 |
| Living costs (visa minimum) | ~$29,710 |
| OSHC health insurance | ~$600–$1,000 |
| Adelaide vs Sydney/Melbourne | **Noticeably cheaper** rent & living |

**Scholarships Ala can realistically get:**

| Scholarship | Value | Notes |
|---|---|---|
| Flinders Foundation auto-scholarship | **20%** off foundation | Automatic; +up to 30% off later degree on results |
| Adelaide Merit Scholarship | **15%** off tuition | Automatic assessment; needs ~ATAR 80 / GPA 4.5 equiv — reachable *after* a strong foundation year |
| Adelaide Global Citizens Scholarship | **15–30%** | Merit-based, automatic assessment |
| TAFE SA Emerging Leaders / Merit | **25% / 15%** | On packaged university offers |

> 💡 **Tip:** A strong **foundation-year result resets Ala's academic record.** Merit scholarships look at the *foundation/diploma* GPA, not her Year 12 marks — so a good foundation year can literally earn her a 15–30% tuition discount that her school grades could not.

- [Adelaide Merit Scholarship (15%)](https://adelaide.edu.au/study/scholarships/int/adelaide-merit-scholarship-15/)`,
    ),

    /* ---- Module 12 ---- */
    page(
      "Module 12 — Adelaide's Hidden Advantage",
      'migration',
      `# Adelaide's Hidden Advantage: Stay & Work After

Adelaide is a full capital city but is classified **"regional"** for migration — a genuine advantage most students don't know:

- **Extra post-study work.** The Temporary Graduate visa (485) gives Adelaide graduates an **extra year** vs Sydney/Melbourne — e.g. ~**3 years** after a bachelor instead of 2 (and a possible further regional extension).
- **State nomination.** South Australia has a broad skilled-occupation list (~500 occupations) and gives **extra points** toward skilled visas (Subclass 190 / 491), plus graduate concessions.
- **Migration points.** Studying + living in a regional area adds points toward permanent-residency pathways.

For a family investing in a degree, this converts study into a realistic **stay-and-work-then-decide** future — without committing to anything now.

- [StudyAdelaide — regional migration advantages](https://studyadelaide.com/work/migration-opportunities)`,
    ),

    /* ---- Module 13 ---- */
    page(
      'Module 13 — The 2027 Action Plan',
      'plan',
      `# The 2027 Action Plan — Month by Month

Today is mid-2026. Here is a realistic timeline to start studying in Australia in **2027**. Foundation programs have several intakes a year, which is what makes 2027 achievable even from here.

| When | Do this |
|---|---|
| **Now – Aug 2026** | ① Complete Year 12. ② Take a free IELTS/PTE practice test to find her starting level. ③ Shortlist 3 pathways: **Adelaide Foundation, Flinders Foundation, one TAFE packaged offer.** |
| **Aug – Oct 2026** | ④ Begin structured IELTS/PTE prep (target 5.5–6.0). ⑤ Gather documents (transcripts, passport, Year 11/12 results). ⑥ Contact the international offices; ask about *Iranian-student payment channels* and *conditional English-bundled offers*. |
| **Oct – Dec 2026** | ⑦ Submit foundation/pathway applications (they're free to hold). ⑧ Sit IELTS/PTE. ⑨ Receive conditional offers. |
| **Dec 2026 – Jan 2027** | ⑩ Meet conditions (English), accept an offer, pay deposit → get **CoE**. ⑪ Arrange OSHC + financial evidence (use the Oman account). |
| **Jan – Feb 2027** | ⑫ Lodge the **Subclass 500** visa with Genuine Student statements. ⑬ Book flights/accommodation once granted. |
| **Feb 2027** | 🎓 **Start Foundation Studies in Adelaide** → guaranteed bachelor entry in 2028. |

*Faster variant:* If her English is already ~5.5 and Year 12 is done, an **8-month foundation starting mid-2027** or a **direct diploma** could compress this further. *Music variant:* add audition-recording prep from Aug 2026 and target IELTS 6.5.

> 💡 **Tip:** Applications and offers cost nothing to hold. **Apply early and to more than one pathway.** The worst case is she has choices; the best case is a scholarship and a smooth visa.`,
    ),

    /* ---- Module 14 ---- */
    page(
      'Module 14 — Document Checklist',
      'documents',
      `# Document Checklist

- [ ] Passport (valid well beyond 2027)
- [ ] Year 11 results + Year 12 completion certificate
- [ ] Certified English translations of all transcripts
- [ ] IELTS/PTE result
- [ ] Financial evidence (bank statements — ideally the Oman account; stable history)
- [ ] Statement of Purpose / Genuine Student answers (draft early)
- [ ] Passport photos
- [ ] OSHC policy (arranged at CoE stage)
- [ ] CV/résumé (helpful for some pathway & scholarship applications)

> ⚠️ **Freshness note:** Fees, visa amounts, entry scores and intake dates change often. Every figure in this guide was accurate as of **July 2026**. Before Ala *applies or pays*, re-check the exact number on the official page. Treat this as a map, not a contract.`,
    ),

    /* ---- Module 15 ---- */
    page(
      'Module 15 — A Note for Ala',
      'personal',
      `# A Note for Ala — You've Got This 🌱

Ala — the low marks are a hard chapter, not the whole story. The Australian system is unusually kind to students who stumbled in one year: it lets your *next* year of study, not your worst one, define you. A strong foundation year can even earn you scholarships your Year 12 grades never could.

You already have three quiet advantages:
- A science stream
- A family base in Oman that solves the hardest money problem
- A voice that will always have a home in Adelaide's choirs and stages

Pick one pathway from this guide, take one practice English test this month, and let the plan carry the rest. One step at a time. 🌱

---

## Sources
*All figures verified July 2026. Re-check official pages before applying or paying.*

- [Adelaide University international entry requirements](https://adelaide.edu.au/study/international-students/how-to-apply/entry-requirements/)
- [Kaplan KICA — Foundation Studies](https://www.kaplancollegeadelaide.edu.au/apply/entry-requirements)
- [Flinders International Foundation Studies](https://www.flinders.edu.au/international/academy/courses/foundation-studies)
- [Torrens University — no-ATAR entry pathways](https://www.torrens.edu.au/new-entry-requirements-2021)
- [TAFE SA — university packaged programs](https://www.tafesa.edu.au/international/university-pathway-programs)
- [Elder Conservatorium — Bachelor of Music](https://adelaide.edu.au/study/degrees/bachelor-of-music-honours-classical-performance/)
- [Student Visa (Subclass 500)](https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/student-500)
- [Australian Govt — information for Iranian students](https://internationaleducation.gov.au/international-network/australia/policyupdates/Pages/Article-InformationforIranianstudents.aspx)
- [Adelaide Merit Scholarship (15%)](https://adelaide.edu.au/study/scholarships/int/adelaide-merit-scholarship-15/)
- [StudyAdelaide — regional migration advantages](https://studyadelaide.com/work/migration-opportunities)`,
    ),
  ]
}
